import { memo, useCallback } from 'react';

import { Button, Pagination, PaginationItem, type PaginationRenderItemParams } from '@mui/material';
import { type Updater } from '@tanstack/react-table';

import ArrowRight from 'assets/icons/icn _ 16px _ Arrow right 2.svg?react';
import ArrowLeft from 'assets/icons/icn _ 24px _ Arrow left 2.svg?react';
import { useStyles as usePaginationActionsStyles } from 'components/tables/dndStandardTable/tableComponents/styles';

type PaginationClasses = 'paginationCaption' | 'menuItem' | 'toolbar' | 'select' | 'action';

interface PaginationActionsProps {
  classNames?: Partial<Record<PaginationClasses, string>>;
  numberOfPages: number;
  pageIndex: number;
  onSetPageIndex: (updater: Updater<number>) => void;
}

const PaginationActions: React.FC<React.PropsWithChildren<PaginationActionsProps>> = ({
  classNames,
  numberOfPages,
  pageIndex,
  onSetPageIndex,
}) => {
  const { classes: paginationActionsClasses, cx } = usePaginationActionsStyles();

  const handlePageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
      onSetPageIndex(index);
    },
    [onSetPageIndex],
  );

  const handlePrevButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (pageIndex + 1 > 1) handlePageChange(event, pageIndex - 1);
    },
    [pageIndex, handlePageChange],
  );

  const handleNextButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (pageIndex + 1 < numberOfPages) handlePageChange(event, pageIndex + 1);
    },
    [pageIndex, numberOfPages, handlePageChange],
  );

  const handleIsSelected = useCallback(
    (item: PaginationRenderItemParams): boolean => {
      return item.page === pageIndex + 1;
    },
    [pageIndex],
  );

  return (
    <div className={classNames?.action || paginationActionsClasses.paginationActions}>
      <Button
        disableRipple
        className={classNames?.action || paginationActionsClasses.buttonPrevious}
        onClick={handlePrevButtonClick}
      >
        <ArrowLeft />
      </Button>
      <Pagination
        hideNextButton
        hidePrevButton
        classes={{ ul: classNames?.action || paginationActionsClasses.itemsContainer }}
        count={numberOfPages}
        defaultPage={1}
        page={pageIndex + 1}
        renderItem={(item: any) => {
          return (
            <PaginationItem
              {...item}
              classes={{ ellipsis: paginationActionsClasses.paginationItemEllipsis }}
              className={cx(
                classNames?.action,
                handleIsSelected(item)
                  ? paginationActionsClasses.paginationItemSelected
                  : paginationActionsClasses.paginationItem,
              )}
              component="button"
              page={item.page}
              selected={handleIsSelected(item)}
              shape="rounded"
              size="medium"
              variant="text"
              onClick={(e) => handlePageChange(e, item.page - 1)}
            />
          );
        }}
        {...(classNames?.action && { siblingCount: 0 })}
      />
      <Button
        disableRipple
        className={classNames?.action || paginationActionsClasses.buttonNext}
        onClick={handleNextButtonClick}
      >
        <ArrowRight />
      </Button>
    </div>
  );
};

export default memo(PaginationActions);
